import { defineStore } from 'pinia'
import { ref } from 'vue'

// Exactly one of the two ids, mirroring a SegmentRun. Both null is "All
// clients". The whole query is null when the page shows filters that no saved
// query matches, and then no row of the navbar query picker is the page's own.
export type PageQuery = {
  segmentId: number | null
  partnerSegmentId: number | null
}

// Shows the query on the page without a reload, and returns false when the
// page leaves it to a page load of `url`, the address of that query. The page
// also loads `url` itself when the query does not load in place.
export type PageQueryLoader = (query: PageQuery, url: string) => boolean

export function sameQuery(a: PageQuery, b: PageQuery): boolean {
  return (
    a.segmentId === b.segmentId && a.partnerSegmentId === b.partnerSegmentId
  )
}

// The Insights companies page writes which query it shows and whether it still
// waits on that query's results. The navbar query picker cannot read either
// from the URL: the page updates the URL a tick after the run starts, and a
// query switched inside the page never reloads the picker.
export const useInsightsPageRun = defineStore('insightsPageRun', () => {
  const running = ref(false)
  const query = ref<PageQuery | null>(null)
  let loader: PageQueryLoader | null = null

  function setPageRun(next: {
    running: boolean
    query: PageQuery | null
  }): void {
    running.value = next.running
    query.value = next.query
  }

  function setQueryLoader(next: PageQueryLoader | null): void {
    loader = next
  }

  // Returns false when no Insights page takes the query in place, and the
  // caller has to navigate to `url` itself.
  function showQuery(next: PageQuery, url: string): boolean {
    return loader ? loader(next, url) : false
  }

  return { running, query, setPageRun, setQueryLoader, showQuery }
})
